// Author: Sparky
(() => {
  const BAR_BREAKPOINT = 1024;
  const CAROUSEL_BREAKPOINT = 768;
  const SPEED = 40; // px/s

  // ─── Bar Marquee ─────────────────────────────────────────────────────────────

  function initBarMarquee() {
    const bar = document.querySelector('[data-sp-bar]');
    if (!bar) return;

    const inner = bar.querySelector('[data-sp-bar-inner]');
    if (!inner) return;

    let marqueeActive = false;
    let animFrame;
    let track = null;

    function buildMarquee() {
      if (marqueeActive) return;
      marqueeActive = true;

      track = document.createElement('div');
      track.style.cssText = 'display:flex;flex-wrap:nowrap;will-change:transform;';

      const clone = inner.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.style.flexShrink = '0';
      inner.style.flexShrink = '0';

      bar.insertBefore(track, inner);
      track.appendChild(inner);
      track.appendChild(clone);
      bar.style.overflow = 'hidden';

      let pos = 0;
      let last = null;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      function tick(ts) {
        if (last !== null) {
          const delta = ts - last;
          pos -= (SPEED * delta) / 1000;
          const w = inner.offsetWidth;
          if (w > 0 && pos <= -w) pos += w;
          track.style.transform = `translateX(${pos}px)`;
        }
        last = ts;
        animFrame = requestAnimationFrame(tick);
      }

      animFrame = requestAnimationFrame(tick);
    }

    function destroyMarquee() {
      if (!marqueeActive) return;
      marqueeActive = false;
      cancelAnimationFrame(animFrame);

      if (track) {
        bar.insertBefore(inner, track);
        track.remove();
        track = null;
        inner.style.flexShrink = '';
      }

      bar.style.overflow = '';
    }

    function check() {
      window.innerWidth < BAR_BREAKPOINT ? buildMarquee() : destroyMarquee();
    }

    check();
    let t;
    window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(check, 150); });
  }

  // ─── Review Carousel ─────────────────────────────────────────────────────────

  function initReviewCarousel() {
    const root = document.querySelector('[data-sp-reviews]');
    if (!root) return;

    const origSlides = Array.from(root.querySelectorAll('[data-sp-reviews-slide]'));
    if (!origSlides.length) return;

    const GAP = 24;   // px between slides
    const PEEK = 32;  // px of adjacent slide visible on each side

    let active = false;
    let track, slides, current, startX, diffX, dragging;

    function slideW() {
      return root.offsetWidth - (PEEK * 2) - (GAP * 2);
    }

    function offsetFor(idx) {
      // Slide[idx] left edge lands at (PEEK + GAP) from container left
      return PEEK + GAP - idx * (slideW() + GAP);
    }

    function go(idx, animate = true) {
      track.style.transition = animate ? 'transform 0.35s ease' : 'none';
      track.style.transform = `translateX(${offsetFor(idx)}px)`;
      current = idx;
    }

    function settle() {
      if (current === 0) go(slides.length - 2, false);
      else if (current === slides.length - 1) go(1, false);
    }

    function applySlideStyle(s) {
      s.style.flexShrink = '0';
      s.style.width = slideW() + 'px';
      s.style.marginRight = GAP + 'px';
    }

    function build() {
      if (active) return;
      active = true;

      track = document.createElement('div');
      track.style.cssText = 'display:flex;will-change:transform;touch-action:pan-y;user-select:none;';

      const cloneLast = origSlides[origSlides.length - 1].cloneNode(true);
      const cloneFirst = origSlides[0].cloneNode(true);

      [cloneLast, ...origSlides, cloneFirst].forEach(s => {
        applySlideStyle(s);
        track.appendChild(s);
      });

      slides = Array.from(track.children);
      current = 1;

      root.style.overflow = 'hidden';
      root.appendChild(track);

      track.addEventListener('transitionend', settle);
      track.addEventListener('pointerdown', onDown);
      track.addEventListener('pointermove', onMove, { passive: false });
      track.addEventListener('pointerup', onUp);
      track.addEventListener('pointercancel', onUp);

      go(current, false);
    }

    function onDown(e) {
      startX = e.clientX;
      diffX = 0;
      dragging = true;
      track.style.transition = 'none';
      track.setPointerCapture(e.pointerId);
    }

    function onMove(e) {
      if (!dragging) return;
      diffX = e.clientX - startX;
      if (Math.abs(diffX) > 5) e.preventDefault();
      track.style.transform = `translateX(${offsetFor(current) + diffX}px)`;
    }

    function onUp() {
      if (!dragging) return;
      dragging = false;
      if (Math.abs(diffX) > 50) diffX < 0 ? go(current + 1) : go(current - 1);
      else go(current);
    }

    function destroy() {
      if (!active) return;
      active = false;

      track.removeEventListener('transitionend', settle);
      track.removeEventListener('pointerdown', onDown);
      track.removeEventListener('pointermove', onMove, { passive: false });
      track.removeEventListener('pointerup', onUp);
      track.removeEventListener('pointercancel', onUp);

      origSlides.forEach(s => {
        s.style.flexShrink = '';
        s.style.width = '';
        s.style.marginRight = '';
        root.appendChild(s);
      });
      track.remove();
      track = null;
      root.style.overflow = '';
    }

    function check() {
      if (window.innerWidth < CAROUSEL_BREAKPOINT) {
        build();
      } else {
        destroy();
      }
    }

    check();
    let t;
    window.addEventListener('resize', () => { clearTimeout(t); t = setTimeout(check, 150); });
  }

  // ─── Glow Timeline ───────────────────────────────────────────────────────────

  function initTimeline(container) {
    const items = container.querySelectorAll('.sp-timeline-item');
    if (!items.length) return;

    const LINE_DURATION = 500;

    items.forEach((item, i) => {
      const content = item.querySelector('.sp-timeline-content');
      const dot = item.querySelector('.sp-timeline-dot');
      const line = item.querySelector('.sp-timeline-line');
      if (i > 0) {
        content.style.opacity = '0';
        content.style.transform = 'translateX(20px)';
        dot.style.opacity = '0';
        dot.style.transform = 'scale(0)';
      }
      content.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
      dot.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      if (line) {
        line.style.transform = 'scaleY(0)';
        line.style.transformOrigin = 'top';
        line.style.transition = `transform ${LINE_DURATION}ms ease`;
      }
    });

    let current = 0;
    const pending = [];

    function revealItem(index) {
      const item = items[index];
      const dot = item.querySelector('.sp-timeline-dot');
      const content = item.querySelector('.sp-timeline-content');
      dot.style.opacity = '1';
      dot.style.transform = 'scale(1)';
      setTimeout(() => {
        content.style.opacity = '1';
        content.style.transform = 'translateX(0)';
      }, 150);
    }

    function drainPending() {
      while (pending.length && pending[0] <= current) {
        pending.shift();
        growLineAndRevealNext(current);
        return;
      }
    }

    function growLineAndRevealNext(index) {
      const line = items[index].querySelector('.sp-timeline-line');
      if (!line || index + 1 >= items.length) return;
      line.style.transform = 'scaleY(1)';
      line.addEventListener('transitionend', function handler(e) {
        if (e.propertyName !== 'transform') return;
        line.removeEventListener('transitionend', handler);
        current = index + 1;
        revealItem(current);
        drainPending();
      });
    }

    revealItem(0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = Array.from(items).indexOf(entry.target);
        observer.unobserve(entry.target);
        const lineOwner = idx - 1;
        if (lineOwner < current) return;
        if (lineOwner === current) {
          growLineAndRevealNext(lineOwner);
        } else {
          pending.push(lineOwner);
          pending.sort((a, b) => a - b);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -20px 0px' });

    for (let i = 1; i < items.length; i++) observer.observe(items[i]);
  }

  function initAllTimelines() {
    document.querySelectorAll('[data-sp-timeline]').forEach(initTimeline);
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    initBarMarquee();
    initReviewCarousel();
    initAllTimelines();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', initAllTimelines);
})();